import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "lucide-react";

import collaboratorApi from "../../../lib/collaboratorApi";
import { EmptyState, PageHeader, Pill, TabSkeleton } from "./ui";

const dayLabel = (date) => {
  if (!date) return "Jour J";
  return new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${date}T12:00:00`));
};

/** Events grouped by day (chronological), each day sorted by time; untimed events last. */
export function groupEventsByDay(events = []) {
  const days = new Map();
  events.forEach((event) => {
    const key = event.date || "";
    if (!days.has(key)) days.set(key, []);
    days.get(key).push(event);
  });
  return Array.from(days, ([date, list]) => ({
    date,
    events: [...list].sort((a, b) => String(a.time || "99:99").localeCompare(String(b.time || "99:99"))),
  })).sort((a, b) => String(a.date || "9999").localeCompare(String(b.date || "9999")));
}

/** The first event that has not started yet, if any. */
export function nextEventId(events = [], now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const upcoming = events
    .filter((event) => event.date && `${event.date}T${event.time || "00:00"}` >= stamp)
    .sort((a, b) => `${a.date}T${a.time || "00:00"}`.localeCompare(`${b.date}T${b.time || "00:00"}`));
  return upcoming[0]?.id ?? null;
}

export default function TimelineTab() {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;
    const fetchTimeline = () => {
      collaboratorApi
        .get("/collaborator-portal/timeline")
        .then((response) => { if (!ignore) setEvents(response.data || []); })
        .catch(() => { if (!ignore) setError("Impossible de charger le planning partagé."); });
    };
    fetchTimeline();
    const intervalId = setInterval(fetchTimeline, 10000);
    return () => { ignore = true; clearInterval(intervalId); };
  }, []);

  const days = useMemo(() => groupEventsByDay(events || []), [events]);
  const upcomingId = useMemo(() => nextEventId(events || []), [events]);

  if (error) return <div role="alert" className="w-full rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">{error}</div>;
  if (!events) return <TabSkeleton />;

  return (
    <div className="w-full space-y-6 pb-8">
      <PageHeader eyebrow="Déroulé de la journée" title="Planning" subtitle="Le déroulé partagé par les mariés, heure par heure, avec les personnes concernées." />

      {days.length === 0 ? (
        <EmptyState icon={Calendar} title="Aucun événement partagé" text="Le planning apparaîtra ici dès que les mariés l'auront renseigné." />
      ) : (
        <div className="space-y-8">
          {days.map((day) => (
            <section key={day.date || "jour-j"} aria-label={dayLabel(day.date)}>
              <h2 className="mb-4 font-serif text-xl capitalize text-[#211d19]">{dayLabel(day.date)}</h2>
              <ol className="space-y-3">
                {day.events.map((event) => (
                  <li key={event.id} className={`grid grid-cols-[64px_minmax(0,1fr)] gap-4 rounded-2xl border p-4 md:grid-cols-[88px_minmax(0,1fr)] md:p-5 ${event.id === upcomingId ? "border-[#bfd0bb] bg-[#f3f8f1] shadow-sm" : "border-[#e9e1d4] bg-white"}`}>
                    <div>
                      <p className="font-serif text-xl leading-none text-[#211d19] md:text-2xl">{event.time || "—"}</p>
                      {event.id === upcomingId ? <div className="mt-2"><Pill tone="success">À suivre</Pill></div> : null}
                    </div>
                    <div className="min-w-0">
                      <h3 className="break-words font-semibold text-[#211d19]">{event.title}</h3>
                      {event.description ? <p className="mt-1 text-sm leading-relaxed text-[#4a4239]">{event.description}</p> : null}
                      {(event.assignees || []).length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {event.assignees.map((assignee) => <Pill key={assignee.id} tone="neutral">{assignee.name || assignee.role || "Collaborateur"}</Pill>)}
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
